import { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { SettingsEmpty, SettingsSaveBar } from './SettingsPanel';
import { useUpdateGradeLevels } from './useUpdateGradeLevels';
import type { GradeLevelsSetting, GradeLevelItem } from './types';

interface GradeLevel {
  description: string;
  order: number;
}

interface GradeLevelsEditorProps {
  schoolId: string | undefined;
  setting?: GradeLevelsSetting;
  onSaved?: () => void;
}

export function GradeLevelsEditor({ schoolId, setting, onSaved }: GradeLevelsEditorProps) {
  const [items, setItems] = useState<GradeLevel[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  const [updateGradeLevels, { loading }] = useUpdateGradeLevels();

  // Initialize from setting
  useEffect(() => {
    const data = setting?.items || (setting?.data as GradeLevelItem[] | undefined) || [];
    // Sort by order, then by index as fallback
    const sorted = [...data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    setItems(sorted);
    setHasChanges(false);
  }, [setting]);

  const handleAdd = () => {
    const maxOrder = items.length > 0 ? Math.max(...items.map(i => i.order ?? 0)) : -1;
    setItems([...items, {
      description: '',
      order: maxOrder + 1,
    }]);
    setHasChanges(true);
  };

  const handleRemove = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const handleChange = (index: number, field: string, value: string) => {
    const newItems = [...items];
    if (field === 'order') {
      newItems[index] = { ...newItems[index], order: parseInt(value) || 0 };
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    setItems(newItems);
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Filter out empty items, sort by order, then save
    const gradeLevels = items
      .filter(item => item.description?.trim())
      .map(({ description, order }) => ({
        description: description.trim(),
        order: order ?? 0,
      }))
      .sort((a, b) => a.order - b.order)
      .map((item, index) => ({
        ...item,
        order: item.order || index,
      }));

    try {
      const { data } = await updateGradeLevels({
        variables: {
          input: {
            schoolId: schoolId!,
            gradeLevels,
          },
        },
      });

      if (data?.updateGradeLevels?.userErrors?.length && data.updateGradeLevels.userErrors.length > 0) {
        console.error('Errors:', data.updateGradeLevels.userErrors);
      } else {
        setHasChanges(false);
        onSaved?.();
      }
    } catch (error) {
      console.error('Error saving grade levels:', error);
    }
  };

  return (
    <>
      {items.length === 0 ? (
        <SettingsEmpty
          action={
            <Button type="button" onClick={handleAdd} className="gap-2">
              <Plus className="h-4 w-4" />
              Add grade level
            </Button>
          }
        >
          No grade levels yet. Add the grades this school enrolls students into.
        </SettingsEmpty>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full min-w-[26rem]">
            <thead className="bg-muted/50">
              <tr>
                <th className="p-3 text-left text-sm font-medium">Grade level</th>
                <th className="w-28 p-3 text-left text-sm font-medium">Order</th>
                <th className="w-12" />
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={index} className="border-t">
                  <td className="p-2">
                    <Input
                      aria-label={`Grade level ${index + 1}`}
                      value={item.description || ''}
                      onChange={(e) => handleChange(index, 'description', e.target.value)}
                      placeholder="e.g. Kindergarten, 1st Grade"
                      className="border-0 focus-visible:ring-1"
                    />
                  </td>
                  <td className="p-2">
                    <Input
                      aria-label={`Order ${index + 1}`}
                      type="number"
                      value={item.order ?? ''}
                      onChange={(e) => handleChange(index, 'order', e.target.value)}
                      className="w-20 border-0 tabular-nums focus-visible:ring-1"
                      placeholder="0"
                    />
                  </td>
                  <td className="p-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemove(index)}
                      aria-label={`Remove grade level ${index + 1}`}
                      className="h-8 w-8"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-3">
          <Button type="button" variant="outline" onClick={handleAdd} className="gap-2">
            <Plus className="h-4 w-4" />
            Add grade level
          </Button>
        </div>
      )}

      <SettingsSaveBar dirty={hasChanges} saving={loading} onSave={handleSave} />
    </>
  );
}

export default GradeLevelsEditor;
